import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { logError } from 'util/logging';
import { classifyAuthError } from './authErrors';
import { reportSessionExpired } from './sessionExpiredBridge';
import config from 'config';

type State = { state: 'fetching' } | { state: 'unauthenticated' } | { state: 'authenticated'; accessToken: string };

// use this hook if you want to do authenticated api calls
// outside of <AuthenticatedRoutes />

export default function useAccessToken() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [accessToken, setToken] = useState<State>({ state: 'fetching' });
  useEffect(() => {
    (async () => {
      if (isLoading) {
        setToken({ state: 'fetching' });
        return;
      }
      if (!isAuthenticated) {
        setToken({ state: 'unauthenticated' });
        return;
      }
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: config.AUTH0_API_IDENTIFIER,
          },
        });
        setToken({ state: 'authenticated', accessToken: token });
      } catch (error) {
        // If we can't get a token (expired, network error, etc), treat as unauthenticated
        // to avoid sending expired/invalid tokens
        const failure = classifyAuthError(error, navigator.onLine);
        const pathname = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

        if (failure.kind === 'terminal') {
          // A cached login in localStorage outliving the Auth0 session is expected and
          // recoverable — it belongs in the session-expired flow, not in the error log.
          reportSessionExpired('token-get');
        } else {
          logError(error, 'Failed to get access token', {
            errorType: failure.code,
            errorDescription: failure.description,
            pathname,
            isAuthenticated,
            isLoading,
          });
        }

        setToken({ state: 'unauthenticated' });
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return [accessToken];
}

export function getAccessTokenOrUndefined(accessToken: State) {
  return accessToken.state === 'authenticated' ? accessToken.accessToken : undefined;
}
